/**
 * @voltxampere/web — `<vxa-pane-presets>` (Sprint 18 Paket B B4).
 *
 * Mockup zenit-platform.html SEKME 3 (PRESETS / HAZIR DEVRELER)
 * tam karşılığı. Modal preset-dialog yerine page-level catalog.
 * Sprint 17'nin <vxa-preset-dialog> halen import/Cmd+Shift+P ile
 * çalışıyor (B8'de deprecate edilecek), bu sayfa onun kalıcı
 * sürümü.
 *
 * Layout:
 *   ┌────────────────────────────────────────┐
 *   │ caption + 64px italic title + lede     │
 *   ├────────────────────────────────────────┤
 *   │ filter chips (Tümü + 6 kategori)       │
 *   ├──────────┬─────────────┬───────────────┤
 *   │  card 1  │   card 2    │    card 3     │
 *   │  card 4  │   card 5    │    card 6     │
 *   │  ...                                   │
 *   └────────────────────────────────────────┘
 *
 * Preset card içeriği (mockup birebir):
 *   - Roman numeral: I., II., III., IV., V. ...
 *   - Mini-canvas SVG thumbnail (B5'te doldurulur — şimdilik
 *     placeholder rectangle)
 *   - TR name + EN italic sub-name
 *   - Italic description (engineering notation values)
 *   - Tag chips (kategori + difficulty stars)
 *
 * Click flow: card → loadPreset(id) → setPane("simulator")
 * Mockup'ta click handler yok ama bizim uygulamada ana yol bu.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import { ALL_PRESETS, CATEGORIES, type Preset, type PresetCategory } from "@voltxampere/presets";

import { selectedCategory, selectCategory, loadPreset } from "../store/preset-loader.js";
import { setPane } from "../store/router.js";
import { currentLocale } from "../store/locale.js";
import { thumbnailFor } from "../canvas/preset-thumbnails.js";
import { t } from "../i18n/index.js";
import { SignalController } from "../lit-utils/signal-controller.js";

const ROMAN_NUMERALS = [
  "I",
  "II",
  "III",
  "IV",
  "V",
  "VI",
  "VII",
  "VIII",
  "IX",
  "X",
  "XI",
  "XII",
  "XIII",
  "XIV",
  "XV",
  "XVI",
  "XVII",
  "XVIII",
  "XIX",
  "XX",
] as const;

@customElement("vxa-pane-presets")
export class VxaPanePresets extends LitElement {
  private readonly _category = new SignalController(this, selectedCategory);
  private readonly _locale = new SignalController(this, currentLocale);
  /**
   * Sprint 26 Lane 9 (S26-PRESETS-ACCORDION) — local UI state for
   * which category accordion sections are currently expanded.
   *
   * Sprint 80.Y L-D-1 (Şefim #5/#6 closure) — default flipped from
   * `[CATEGORIES[0]?.id]` (only "basic") to ALL CATEGORIES so the
   * pane reveals all 31 presets organized into their category
   * sections at first paint. This pairs with selectedCategory's
   * default flip from "basic" to null (preset-loader.ts) to take
   * the renderAccordion path. Users can collapse sections they
   * don't need (Sprint 26 "focus" intent preserved on demand).
   * NOT persisted to IDB.
   */
  @state()
  private _expandedCategories: ReadonlySet<PresetCategory> = new Set(CATEGORIES.map((c) => c.id));

  static override styles = css`
    :host {
      display: block;
      height: 100%;
      overflow-y: auto;
      overflow-x: hidden;
      background: var(--void);
      color: var(--ivory);
    }

    .wrap {
      max-width: 1400px;
      margin: 0 auto;
      padding: 60px var(--s-9) 120px;
    }

    /* ─────────────────── HEADER ─────────────────── */
    .header {
      margin-bottom: 48px;
    }

    .caption {
      display: flex;
      align-items: center;
      gap: var(--s-4);
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.3em;
      color: var(--brass);
      text-transform: uppercase;
      margin-bottom: 20px;
    }

    .caption .dot {
      width: 4px;
      height: 4px;
      background: var(--brass);
      border-radius: 50%;
    }

    .title {
      font-family: var(--font-display);
      font-weight: 300;
      font-size: 64px;
      color: var(--ivory);
      line-height: 1;
      letter-spacing: -0.015em;
      margin: 0 0 14px;
    }

    .title em {
      color: var(--brass);
      font-style: italic;
    }

    .sub {
      font-size: 18px;
      color: var(--ivory-dim);
      font-style: italic;
      max-width: 600px;
      margin: 0;
    }

    /* ─────────────────── FILTERS ─────────────────── */
    .filters {
      display: flex;
      gap: 1px;
      margin: 48px 0 36px;
      background: var(--ivory-wisp);
      border: 1px solid var(--ivory-wisp);
      flex-wrap: wrap;
    }

    .filter-chip {
      padding: 12px 20px;
      background: var(--void);
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.22em;
      color: var(--ivory-dim);
      text-transform: uppercase;
      cursor: pointer;
      transition:
        color 160ms,
        background 160ms;
      border: none;
    }

    .filter-chip:hover {
      color: var(--ivory);
    }

    .filter-chip[aria-pressed="true"] {
      color: var(--brass);
      background: var(--ink);
    }

    .filter-chip:focus-visible {
      outline: 1px solid var(--brass);
      outline-offset: -1px;
    }

    /* ─────────────────── ACCORDION ──────────────────
       Sprint 26 Lane 9 (S26-PRESETS-ACCORDION). Each category
       renders as a vertically stacked section: a clickable header
       (chevron + name + count) and a content panel that expands
       into the existing card grid. The 200 ms cubic-bezier
       collapse animation is grid-row driven so we do not need
       JS measurement; the prefers-reduced-motion: reduce media
       query zeros the transition for users who opt out of motion. */
    .category-section {
      border: 1px solid var(--ivory-wisp);
    }
    .category-section + .category-section {
      border-top: 0;
    }

    .category-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      width: 100%;
      padding: 16px 20px;
      background: var(--void);
      color: var(--ivory);
      border: 0;
      font-family: var(--font-mono);
      font-size: 11px;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      cursor: pointer;
      text-align: left;
      transition:
        color var(--transition-hover),
        background var(--transition-hover);
    }

    .category-header:hover {
      color: var(--brass);
      background: var(--ink-2, var(--void));
    }

    .category-header:focus-visible {
      outline: 1px solid var(--brass);
      outline-offset: -2px;
    }

    .category-header .category-name {
      flex: 1;
    }

    .category-header .count {
      color: var(--ivory-dim);
      font-size: 10px;
      letter-spacing: 0.18em;
    }

    .category-header .chevron {
      color: var(--brass-faint);
      transition: transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1);
      font-family: var(--font-mono);
      font-size: 12px;
      line-height: 1;
    }
    .category-section[data-open="true"] .category-header .chevron {
      transform: rotate(90deg);
      color: var(--brass);
    }

    .category-panel {
      display: grid;
      grid-template-rows: 0fr;
      transition: grid-template-rows 200ms cubic-bezier(0.2, 0.8, 0.2, 1);
    }
    .category-section[data-open="true"] .category-panel {
      grid-template-rows: 1fr;
    }
    .category-panel > .category-panel-inner {
      overflow: hidden;
      min-height: 0;
    }

    @media (prefers-reduced-motion: reduce) {
      .category-header .chevron,
      .category-panel {
        transition-duration: 0s;
      }
    }

    /* ─────────────────── GRID ─────────────────── */
    .grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 1px;
      background: var(--ivory-wisp);
      border: 1px solid var(--ivory-wisp);
    }

    .card {
      background: var(--ink);
      /* Sprint 26 Lane 7 R5-VIS-7 — canonical horizontal 32 (was 28, 8-pt grid). */
      padding: 32px 32px;
      cursor: pointer;
      transition:
        background 240ms ease,
        outline-color 200ms ease;
      position: relative;
      min-height: 320px;
      display: flex;
      flex-direction: column;
      border: none;
      text-align: left;
      color: inherit;
      outline: 1px solid transparent;
      outline-offset: -1px;
    }

    .card:hover {
      background: var(--ink-2);
    }

    .card:focus-visible {
      outline-color: var(--brass);
    }

    .num {
      font-family: var(--font-display);
      font-style: italic;
      font-size: 13px;
      color: var(--brass);
      letter-spacing: 0.1em;
      margin-bottom: 14px;
    }

    .art {
      height: 140px;
      margin-bottom: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-top: 1px dotted var(--ink-3);
      border-bottom: 1px dotted var(--ink-3);
      padding: 10px 0;
      color: var(--ivory-dim);
      font-family: var(--font-mono);
      font-size: 9px;
      letter-spacing: 0.18em;
      text-transform: uppercase;
    }

    .name {
      font-family: var(--font-display);
      font-weight: 500;
      font-size: 24px;
      color: var(--ivory);
      letter-spacing: -0.005em;
      margin-bottom: 6px;
      line-height: 1.1;
    }

    .name .en {
      color: var(--ivory-dim);
      font-style: italic;
      font-size: 15px;
      font-weight: 400;
      margin-left: 4px;
    }

    .desc {
      font-size: 13px;
      color: var(--ivory-dim);
      line-height: 1.55;
      font-style: italic;
      margin-bottom: 16px;
      flex: 1;
    }

    .desc em {
      color: var(--brass);
      font-style: italic;
    }

    .tags {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }

    .tag {
      font-family: var(--font-mono);
      font-size: 9px;
      letter-spacing: 0.15em;
      color: var(--ivory-faint);
      text-transform: uppercase;
      padding: 3px 8px;
      border: 1px solid var(--ivory-wisp);
    }

    .tag.difficulty {
      color: var(--brass);
      border-color: var(--brass-faint);
    }

    /* ─────────────────── EMPTY ─────────────────── */
    .empty {
      grid-column: 1 / -1;
      padding: 80px var(--s-7);
      text-align: center;
      font-family: var(--font-body);
      font-style: italic;
      font-size: 16px;
      color: var(--ivory-dim);
      background: var(--ink);
    }

    /* ─────────────────── RESPONSIVE ─────────────────── */
    @media (max-width: 1024px) {
      .grid {
        grid-template-columns: repeat(2, 1fr);
      }

      .title {
        font-size: 48px;
      }
    }

    @media (max-width: 640px) {
      .wrap {
        padding: 40px var(--s-5) 80px;
      }

      .grid {
        grid-template-columns: 1fr;
      }

      .title {
        font-size: 36px;
      }
    }
  `;

  override render() {
    const cat = this._category.value;
    const locale = this._locale.value;
    // Sprint 80.Y L-D-1 — total preset + category count badge.
    // Closes Şefim's #5/#6 discoverability gap: users on first
    // paint see "31 preset · 7 kategori" so they understand the
    // total library size before they even click a filter chip.
    const totalCount = ALL_PRESETS.length;
    const categoryCount = CATEGORIES.length;

    return html`
      <div class="wrap">
        <div class="header">
          <div class="caption">
            <span class="dot" aria-hidden="true"></span>
            <span>${t("presets.page.caption")}</span>
            <span aria-hidden="true">·</span>
            <span>III · MMXXVI</span>
            <span aria-hidden="true">·</span>
            <span data-testid="preset-total-badge"
              >${t("presets.page.totalBadge", {
                count: String(totalCount),
                categories: String(categoryCount),
              })}</span
            >
          </div>
          <h1 class="title">${this.renderTitle()}</h1>
          <p class="sub">${t("presets.page.sub")}</p>
        </div>

        <div class="filters" role="tablist" @click=${this._onFilterClick}>
          ${this.renderFilters(cat)}
        </div>

        ${cat === null
          ? this.renderAccordion(locale)
          : this.renderFlatGrid(this.presetsInCategory(cat), locale)}
      </div>
    `;
  }

  /**
   * Sprint 26 Lane 9 — accordion view shown when the user picks
   * "Tümü". Each CATEGORIES entry becomes a stacked section with a
   * clickable header. Default-expanded set is initialised to the
   * first category id (see `_expandedCategories` field) so the
   * pane lands on the F-IA-13 ~3-4 visible card target.
   */
  private renderAccordion(locale: "tr" | "en") {
    return html`<div class="accordion">
      ${CATEGORIES.map((c) => {
        const presets = this.presetsInCategory(c.id);
        if (presets.length === 0) return nothing;
        const open = this._expandedCategories.has(c.id);
        const name = locale === "tr" ? c.name.tr : c.name.en;
        const titleAttr = `${name} (${String(presets.length)})`;
        return html`<section class="category-section" data-cat-id=${c.id} data-open=${open}>
          <button
            type="button"
            class="category-header"
            data-action="toggle-category"
            data-cat-id=${c.id}
            aria-expanded=${open}
            aria-controls=${`category-panel-${c.id}`}
            title=${titleAttr}
            @click=${this._onCategoryHeaderClick}
          >
            <span class="chevron" aria-hidden="true">▶</span>
            <span class="category-name">${name}</span>
            <span class="count">${String(presets.length)}</span>
          </button>
          <div class="category-panel" id=${`category-panel-${c.id}`} role="region">
            <div class="category-panel-inner">
              <div class="grid" @click=${this._onCardClick}>
                ${presets.map((p) => this.renderCard(p, locale, ALL_PRESETS.indexOf(p)))}
              </div>
            </div>
          </div>
        </section>`;
      })}
    </div>`;
  }

  /** Pre-Lane-9 flat-grid view, kept for the "single-category"
   * filter path so a chip click still produces the focused
   * single-grid layout. */
  private renderFlatGrid(visible: readonly Preset[], locale: "tr" | "en") {
    return html`<div class="grid" @click=${this._onCardClick}>
      ${visible.length === 0
        ? html`<div class="empty">${t("presets.empty")}</div>`
        : visible.map((p, i) => this.renderCard(p, locale, i))}
    </div>`;
  }

  private presetsInCategory(cat: PresetCategory): readonly Preset[] {
    return ALL_PRESETS.filter((p) => p.category === cat);
  }

  private readonly _onCategoryHeaderClick = (e: Event): void => {
    const target = e.currentTarget as HTMLElement | null;
    if (!target) return;
    const id = target.dataset.catId ?? "";
    this.toggleCategory(id);
  };

  /**
   * Sprint 26 Lane 9 — public toggle helper used by the click
   * handler and by tests. Adds / removes the id from
   * `_expandedCategories`; ignores unknown ids defensively.
   */
  toggleCategory(id: string): void {
    const known = CATEGORIES.find((c) => c.id === id);
    if (!known) return;
    const next = new Set(this._expandedCategories);
    if (next.has(known.id)) next.delete(known.id);
    else next.add(known.id);
    this._expandedCategories = next;
  }

  /** Test-only inspector — read-only snapshot of the open set. */
  get expandedCategories(): ReadonlySet<PresetCategory> {
    return this._expandedCategories;
  }

  private renderTitle() {
    // i18n string contains <em>...</em> for the brass emphasis.
    return this.renderRichText(t("presets.page.title"));
  }

  private renderFilters(active: PresetCategory | null) {
    return [
      html`<button
        class="filter-chip"
        type="button"
        role="tab"
        data-action="filter"
        data-cat="all"
        aria-pressed=${active === null}
      >
        ${t("presets.category.all")}
      </button>`,
      ...CATEGORIES.map(
        (c) =>
          html`<button
            class="filter-chip"
            type="button"
            role="tab"
            data-action="filter"
            data-cat=${c.id}
            aria-pressed=${active === c.id}
          >
            ${this._locale.value === "tr" ? c.name.tr : c.name.en}
          </button>`,
      ),
    ];
  }

  private renderCard(p: Preset, locale: "tr" | "en", index: number) {
    const numeral = ROMAN_NUMERALS[index] ?? String(index + 1);
    const stars = "★".repeat(p.difficulty);
    return html`
      <button
        class="card"
        type="button"
        data-action="load"
        data-preset-id=${p.id}
        data-testid=${`preset-card-${p.id}`}
        aria-label=${locale === "tr" ? p.name.tr : p.name.en}
        title=${locale === "tr" ? p.name.tr : p.name.en}
      >
        <div class="num">${numeral} ·</div>
        <div class="art" aria-hidden="true">${this.renderArtPlaceholder(p)}</div>
        <div class="name">
          ${locale === "tr" ? p.name.tr : p.name.en}
          ${locale === "tr" && p.name.en !== p.name.tr
            ? html`<span class="en">${p.name.en}</span>`
            : nothing}
        </div>
        <div class="desc">
          ${this.renderRichText(locale === "tr" ? p.description.tr : p.description.en)}
        </div>
        <div class="tags">
          <div class="tag">${this.categoryName(p.category, locale)}</div>
          <div class="tag difficulty">${stars}</div>
        </div>
      </button>
    `;
  }

  /**
   * B5 — bespoke mini-canvas SVG thumbnails per preset.id.
   * `thumbnailFor()` returns the preset's symbol or the default
   * "CIRCUIT" placeholder if the preset is registered without a
   * thumbnail (forces visibility of the gap during dev).
   */
  private renderArtPlaceholder(p: Preset) {
    return thumbnailFor(p.id);
  }

  private categoryName(id: PresetCategory, locale: "tr" | "en"): string {
    const cat = CATEGORIES.find((c) => c.id === id);
    if (!cat) return id;
    return locale === "tr" ? cat.name.tr : cat.name.en;
  }

  private renderRichText(text: string): unknown[] {
    const parts: unknown[] = [];
    const re = /<em>([\s\S]*?)<\/em>/g;
    let last = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(text)) !== null) {
      const before = text.slice(last, match.index);
      if (before.length > 0) parts.push(before);
      parts.push(html`<em>${match[1]}</em>`);
      last = match.index + match[0].length;
    }
    const tail = text.slice(last);
    if (tail.length > 0) parts.push(tail);
    return parts.length > 0 ? parts : [text];
  }

  private readonly _onFilterClick = (e: Event): void => {
    const path = e.composedPath();
    const target = path.find(
      (n): n is HTMLElement => n instanceof HTMLElement && n.dataset.action === "filter",
    );
    if (!target) return;
    const cat = target.dataset.cat;
    if (cat === undefined) return;
    if (cat === "all") {
      selectCategory(null);
    } else {
      selectCategory(cat as PresetCategory);
    }
  };

  private readonly _onCardClick = (e: Event): void => {
    const path = e.composedPath();
    const target = path.find(
      (n): n is HTMLElement => n instanceof HTMLElement && n.dataset.action === "load",
    );
    if (!target) return;
    const id = target.dataset.presetId;
    if (id === undefined) return;
    const ok = loadPreset(id);
    if (ok) {
      // Close the (legacy) dialog if it happened to be open and
      // route to the simulator so the user sees the loaded circuit
      // immediately.
      setPane("simulator");
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-pane-presets": VxaPanePresets;
  }
}
